import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import {
  listDeployments,
  createDeployment,
  deleteDeployment,
  renameDeployment,
  getDeploymentHistory,
  getDeploymentMetrics,
  getDeploymentMetricsHistory,
  getDeploymentHaltHistory,
  updateRelease,
  promoteRelease,
  rollbackDeployment,
  type Deployment,
  type DeploymentMetrics,
  type DeploymentMetricsHistory,
  type HaltHistoryResponse,
  type Package,
  type PackageInfo,
} from "../api/deployments.api";
import { ApiError } from "@/lib/api/client";
import { haltHistoryRefetchInterval, haltHistoryRetry, isReleaseHalted } from "../lib/halt-history";
import { appsKeys } from "@/features/apps/hooks/useApps";

export const deploymentsKeys = {
  all: (appName: string) => ["deployments", appName] as const,
  list: (appName: string) => [...deploymentsKeys.all(appName), "list"] as const,
  history: (appName: string, deploymentName: string) =>
    [...deploymentsKeys.all(appName), "history", deploymentName] as const,
  metrics: (appName: string, deploymentName: string) =>
    [...deploymentsKeys.all(appName), "metrics", deploymentName] as const,
  heldMetrics: (appName: string, deploymentName: string) =>
    [...deploymentsKeys.all(appName), "metrics-held", deploymentName] as const,
  metricsHistory: (appName: string, deploymentName: string) =>
    [...deploymentsKeys.all(appName), "metrics-history", deploymentName] as const,
  haltHistory: (appName: string, deploymentName: string) =>
    [...deploymentsKeys.all(appName), "halt-history", deploymentName] as const,
};

export interface HeldDeploymentMetrics {
  metrics: DeploymentMetrics;
  receivedAt: number;
  deploymentKey: string | undefined;
}

function freedDeploymentQueryKeys(appName: string, deploymentName: string) {
  return [
    deploymentsKeys.history(appName, deploymentName),
    deploymentsKeys.metrics(appName, deploymentName),
    deploymentsKeys.metricsHistory(appName, deploymentName),
    deploymentsKeys.haltHistory(appName, deploymentName),
  ] as const;
}

function removeFreedDeploymentQueries(
  queryClient: QueryClient,
  appName: string,
  deploymentName: string,
) {
  queryClient.removeQueries({ queryKey: deploymentsKeys.heldMetrics(appName, deploymentName) });
  const keys = freedDeploymentQueryKeys(appName, deploymentName);
  for (const queryKey of keys) {
    queryClient.cancelQueries({ queryKey });
    queryClient.removeQueries({ queryKey, type: "inactive" });
  }
  const leftover = () => keys.filter((queryKey) => queryClient.getQueryCache().find({ queryKey }));
  if (leftover().length === 0) return;
  const cache = queryClient.getQueryCache();
  const unsubscribe = cache.subscribe((event) => {
    if (event.type !== "observerRemoved" && event.type !== "removed") return;
    for (const queryKey of leftover()) {
      queryClient.removeQueries({ queryKey, type: "inactive" });
    }
    if (leftover().length === 0) unsubscribe();
  });
}

export interface CreateDeploymentInput {
  name: string;
  key?: string;
}

export type CreateDeploymentVariables = string | CreateDeploymentInput;

export function useDeployments(appName: string) {
  return useQuery<{ deployments: Deployment[] }, ApiError>({
    queryKey: deploymentsKeys.list(appName),
    queryFn: () => listDeployments(appName),
    enabled: !!appName,
  });
}

export function useCreateDeployment(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<{ deployment: Deployment }, ApiError, CreateDeploymentVariables>({
    mutationFn: (variables) => {
      const { name, key } = typeof variables === "string" ? { name: variables } : variables;
      return createDeployment(appName, name, key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentsKeys.all(appName) });
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
    },
  });
}

export function useDeleteDeployment(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (deploymentName) => deleteDeployment(appName, deploymentName),
    onSuccess: (_, deploymentName) => {
      queryClient.invalidateQueries({ queryKey: deploymentsKeys.list(appName) });
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      removeFreedDeploymentQueries(queryClient, appName, deploymentName);
    },
  });
}

export function useRenameDeployment(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { deployment: Deployment },
    ApiError,
    { deploymentName: string; newName: string }
  >({
    mutationFn: ({ deploymentName, newName }) => renameDeployment(appName, deploymentName, newName),
    onSuccess: (_, { deploymentName, newName }) => {
      queryClient.invalidateQueries({ queryKey: deploymentsKeys.list(appName) });
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      removeFreedDeploymentQueries(queryClient, appName, deploymentName);
      removeFreedDeploymentQueries(queryClient, appName, newName);
    },
  });
}

export function useDeploymentHistory(appName: string, deploymentName: string) {
  return useQuery<{ history: Package[] }, ApiError>({
    queryKey: deploymentsKeys.history(appName, deploymentName),
    queryFn: () => getDeploymentHistory(appName, deploymentName),
    enabled: !!appName && !!deploymentName,
  });
}

function isTerminalCurrentMetricsError(query: { state: { error: ApiError | null } }) {
  const status = query.state.error?.status;
  return status === 401 || status === 403 || status === 404;
}

function currentMetricsRefetchInterval(query: { state: { error: ApiError | null } }) {
  return isTerminalCurrentMetricsError(query) ? false : 1000 * 60;
}

function currentMetricsRefetchOnFocus(query: { state: { error: ApiError | null } }) {
  return isTerminalCurrentMetricsError(query) ? false : "always";
}

export function useDeploymentHaltHistory(appName: string, deploymentName: string) {
  return useQuery<HaltHistoryResponse, ApiError>({
    queryKey: deploymentsKeys.haltHistory(appName, deploymentName),
    queryFn: () => getDeploymentHaltHistory(appName, deploymentName),
    enabled: !!appName && !!deploymentName,
    retry: haltHistoryRetry,
    staleTime: 0,
    refetchInterval: (query) => haltHistoryRefetchInterval(query.state.error),
  });
}

export function useDeploymentMetrics(appName: string, deploymentName: string) {
  return useQuery<{ metrics: DeploymentMetrics }, ApiError>({
    queryKey: deploymentsKeys.metrics(appName, deploymentName),
    queryFn: () => getDeploymentMetrics(appName, deploymentName),
    enabled: !!appName && !!deploymentName,
    refetchInterval: currentMetricsRefetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: currentMetricsRefetchOnFocus,
    refetchOnReconnect: currentMetricsRefetchOnFocus,
  });
}

function hasEntries(metrics: DeploymentMetrics | undefined): boolean {
  return metrics !== undefined && Object.keys(metrics).length > 0;
}

function readableDeploymentKey(key: string | undefined): string | undefined {
  return key && key !== "(hidden)" ? key : undefined;
}

function useIsInvalidated(queryKey: QueryKey): boolean {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  );
  return useSyncExternalStore(
    subscribe,
    () => queryClient.getQueryState(queryKey)?.isInvalidated === true,
  );
}

interface HeldRecordEvidence {
  listedKey: string | undefined;
  listIsInvalidated: boolean;
  recordIsInvalidated: boolean;
  trustedInThisView: boolean;
}

function isHeldRecordTrusted(record: HeldDeploymentMetrics, evidence: HeldRecordEvidence): boolean {
  if (record.deploymentKey !== undefined && evidence.listedKey !== undefined) {
    if (record.deploymentKey !== evidence.listedKey) return false;
    return !evidence.listIsInvalidated || evidence.trustedInThisView;
  }
  return !evidence.recordIsInvalidated || evidence.trustedInThisView;
}

export function useHeldDeploymentMetrics(appName: string, deploymentName: string) {
  const queryClient = useQueryClient();
  const query = useDeploymentMetrics(appName, deploymentName);
  const deploymentsQuery = useDeployments(appName);
  const heldQuery = useQuery<HeldDeploymentMetrics>({
    queryKey: deploymentsKeys.heldMetrics(appName, deploymentName),
    queryFn: skipToken,
  });
  const listIsInvalidated = useIsInvalidated(deploymentsKeys.list(appName));
  const recordIsInvalidated = useIsInvalidated(
    deploymentsKeys.heldMetrics(appName, deploymentName),
  );

  const owner = JSON.stringify([appName, deploymentName]);
  const [trustedOwner, setTrustedOwner] = useState<string | undefined>(undefined);
  if (trustedOwner !== undefined && trustedOwner !== owner) {
    setTrustedOwner(undefined);
  }
  const trustedInThisView = trustedOwner === owner;

  const listedKey = readableDeploymentKey(
    deploymentsQuery.data?.deployments.find((deployment) => deployment.name === deploymentName)
      ?.key,
  );
  const latest = query.isError ? undefined : query.data?.metrics;
  const latestIsTrusted = query.isSuccess && (query.isFetchedAfterMount || !query.isStale);
  const trustedLatest = latestIsTrusted && hasEntries(latest) ? latest : undefined;
  const receivedAt = query.dataUpdatedAt;

  const listed = deploymentsQuery.data?.deployments.some(
    (deployment) => deployment.name === deploymentName,
  );
  useEffect(() => {
    if (!trustedLatest) return;
    if (listIsInvalidated) return;
    if (!listed) return;
    const key = deploymentsKeys.heldMetrics(appName, deploymentName);
    const stored = queryClient.getQueryData<HeldDeploymentMetrics>(key);
    if (
      stored?.receivedAt === receivedAt &&
      (stored.deploymentKey !== undefined || listedKey === undefined)
    ) {
      return;
    }
    queryClient.setQueryData<HeldDeploymentMetrics>(key, {
      metrics: trustedLatest,
      receivedAt,
      deploymentKey: listedKey,
    });
  }, [
    queryClient,
    appName,
    deploymentName,
    trustedLatest,
    receivedAt,
    listedKey,
    listIsInvalidated,
    listed,
  ]);

  const record = heldQuery.data;
  const recordIsTrusted =
    record !== undefined &&
    isHeldRecordTrusted(record, {
      listedKey,
      listIsInvalidated,
      recordIsInvalidated,
      trustedInThisView,
    });
  if (recordIsTrusted && !trustedInThisView) {
    setTrustedOwner(owner);
  }
  const held =
    recordIsTrusted &&
    !hasEntries(latest) &&
    !isTerminalCurrentMetricsError({ state: { error: query.error } })
      ? record
      : undefined;

  const listNeedsRecheck =
    held?.deploymentKey !== undefined &&
    deploymentsQuery.isStale &&
    !deploymentsQuery.isFetching &&
    (!deploymentsQuery.isError || deploymentsQuery.errorUpdatedAt < query.dataUpdatedAt);
  const refetchList = deploymentsQuery.refetch;
  useEffect(() => {
    if (listNeedsRecheck) void refetchList();
  }, [listNeedsRecheck, refetchList]);

  const listIsRefetching = deploymentsQuery.isFetching || deploymentsQuery.isPending;
  const isSettling =
    record !== undefined &&
    !hasEntries(latest) &&
    !isTerminalCurrentMetricsError({ state: { error: query.error } }) &&
    !recordIsTrusted &&
    record.deploymentKey !== undefined &&
    listedKey !== undefined &&
    record.deploymentKey === listedKey &&
    listIsInvalidated &&
    listIsRefetching;

  return {
    query,
    metrics: held ? held.metrics : latest,
    heldSince: held?.receivedAt,
    isSettling,
  };
}

export function useDeploymentMetricsHistory(appName: string, deploymentName: string) {
  return useQuery<DeploymentMetricsHistory, ApiError>({
    queryKey: deploymentsKeys.metricsHistory(appName, deploymentName),
    queryFn: () => getDeploymentMetricsHistory(appName, deploymentName),
    enabled: !!appName && !!deploymentName,
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateRelease(appName: string, deploymentName: string) {
  const queryClient = useQueryClient();

  return useMutation<{ package: Package } | undefined, ApiError, PackageInfo>({
    mutationFn: (packageInfo) => updateRelease(appName, deploymentName, packageInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentsKeys.all(appName) });
    },
    onError: (error) => {
      if (error.status === 409) refreshHaltedRelease(queryClient, appName, deploymentName);
    },
  });
}

function refreshHaltedRelease(queryClient: QueryClient, appName: string, deploymentName: string) {
  queryClient.invalidateQueries({ queryKey: deploymentsKeys.history(appName, deploymentName) });
  queryClient.invalidateQueries({ queryKey: deploymentsKeys.haltHistory(appName, deploymentName) });
}

export function usePromoteRelease(appName: string, sourceDeploymentName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { package: Package },
    ApiError,
    { destination: string; packageInfo?: PackageInfo }
  >({
    mutationFn: ({ destination, packageInfo }) =>
      promoteRelease(appName, sourceDeploymentName, destination, packageInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentsKeys.all(appName) });
    },
    onError: (error) => {
      if (isReleaseHalted(error)) refreshHaltedRelease(queryClient, appName, sourceDeploymentName);
    },
  });
}

export function useRollbackDeployment(appName: string, deploymentName: string) {
  const queryClient = useQueryClient();

  return useMutation<{ package: Package }, ApiError, string | undefined>({
    mutationFn: (targetRelease) => rollbackDeployment(appName, deploymentName, targetRelease),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentsKeys.all(appName) });
    },
    onError: (error) => {
      if (isReleaseHalted(error)) refreshHaltedRelease(queryClient, appName, deploymentName);
    },
  });
}
